import React, { useState, useMemo, useCallback } from "react";
import { RELATIONSHIP_OPTIONS } from "../../../../constants/membersConstants";
import { useRelationshipPrefillMany } from "../../../../hooks/useRelationshipPrefill";

const isBlank = (val) => val === null || val === undefined || val === "";

const getMemberName = (members, memberId) => {
  const m = members?.find((x) => x.id === memberId);
  if (!m?.personal_info) return "Member";
  const fn = m.personal_info.first_name || "";
  const ln = m.personal_info.last_name || "";
  return `${fn} ${ln}`.trim() || "Member";
};

const fieldMissing = (tc) => ({
  household_member_id: isBlank(tc?.household_member_id),
  relationship: isBlank(tc?.relationship),
});

const TrustedContactsForm = ({
  householdData,
  fieldData,
  entityId,
  onSave,
  onSaveAndExit,
  saving,
  nextLabel,
  clientToken,
}) => {
  const account = fieldData || {};
  const existingContacts = useMemo(
    () => Array.isArray(account.trusted_contacts) ? account.trusted_contacts : [],
    [account.trusted_contacts],
  );
  const members = useMemo(() => householdData?.members || [], [householdData]);

  // Owner member IDs — excluded from trusted contact member dropdown
  const ownerMemberIds = useMemo(() => {
    const ids = new Set();
    (account.owners_info || []).forEach((o) => { if (o.id) ids.add(o.id); });
    return ids;
  }, [account.owners_info]);

  const [formData, setFormData] = useState(() =>
    existingContacts.map((tc) => ({
      household_member_id: tc.household_member_id || "",
      relationship: tc.relationship || "",
    })),
  );

  const [error, setError] = useState("");

  const rowsWithMissing = useMemo(() => {
    return existingContacts
      .map((tc, idx) => ({ tc, idx, missing: fieldMissing(tc) }))
      .filter(({ missing }) => Object.values(missing).some(Boolean));
  }, [existingContacts]);

  const totalMissing = rowsWithMissing.reduce(
    (sum, { missing }) => sum + Object.values(missing).filter(Boolean).length,
    0,
  );

  const handleChange = useCallback((idx, field, value) => {
    setFormData((prev) => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], [field]: value };
      return updated;
    });
    setError("");
  }, []);

  useRelationshipPrefillMany({
    householdId: householdData?.id,
    accountId: entityId,
    clientToken: clientToken || null,
    rows: formData,
    getMemberId: (row) => row?.household_member_id,
    getCurrentValue: (row) => row?.relationship,
    onPrefill: (idx, relationship) => handleChange(idx, "relationship", relationship),
  });

  const validate = useCallback(() => {
    for (let i = 0; i < formData.length; i++) {
      const row = formData[i];
      const orig = existingContacts[i];

      const hm = row.household_member_id || orig?.household_member_id;
      const rel = row.relationship || orig?.relationship;

      if (isBlank(hm) || isBlank(rel)) {
        setError("Complete household member and relationship for each trusted contact before saving.");
        return false;
      }
    }
    return true;
  }, [formData, existingContacts]);

  const buildPayload = useCallback(() => {
    const toUpdate = [];
    const toAdd = [];

    for (let i = 0; i < existingContacts.length; i++) {
      const orig = existingContacts[i];
      const row = formData[i] || {};

      const household_member_id = row.household_member_id || orig.household_member_id;
      const relationship = row.relationship || orig.relationship;

      const isNew = !orig.id || orig.__isNew;
      if (isNew) {
        if (household_member_id) {
          toAdd.push({ household_member_id, relationship });
        }
      } else {
        toUpdate.push({ id: orig.id, household_member_id, relationship });
      }
    }

    const tcPayload = {};
    if (toAdd.length > 0) {
      tcPayload.create = toAdd;
    }
    if (toUpdate.length > 0) {
      tcPayload.update = {};
      for (const row of toUpdate) {
        tcPayload.update[row.id] = {
          household_member_id: row.household_member_id,
          relationship: row.relationship,
        };
      }
    }

    return {
      accounts: {
        [entityId]: Object.keys(tcPayload).length > 0
          ? { trusted_contacts: tcPayload }
          : {},
      },
    };
  }, [existingContacts, formData, entityId]);

  const handleSubmit = (cb) => {
    if (!validate()) return;
    cb(buildPayload());
  };

  if (existingContacts.length === 0) {
    return (
      <div className="text-center py-6 text-gray-500">
        <p className="text-sm">No trusted contacts on this account.</p>
      </div>
    );
  }

  if (rowsWithMissing.length === 0) {
    return (
      <div className="text-center py-6 text-gray-500">
        <p className="text-sm">All trusted contact details have been filled.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-lg font-semibold text-gray-900">Missing Information</h2>
        <span className="text-sm text-red-500 font-medium">
          ● {totalMissing} Required field{totalMissing !== 1 ? "s" : ""} missing
        </span>
      </div>

      <p className="text-sm text-gray-600 mb-4">
        Complete the missing trusted contact fields below. Only empty fields are shown for each row.
      </p>

      {error && (
        <div className="mb-4 flex items-start gap-3 px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
          {error}
        </div>
      )}

      {rowsWithMissing.map(({ tc, idx, missing }) => {
        const row = formData[idx] || {};
        const memberName = getMemberName(members, tc?.household_member_id || row.household_member_id);

        return (
          <div
            key={tc?.id || `tc-${idx}`}
            className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm mb-4"
          >
            <div className="bg-gray-50 border-b border-gray-200 px-4 py-3 flex justify-between items-center">
              <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2">
                <span className="bg-indigo-100 text-indigo-700 w-6 h-6 rounded-full flex items-center justify-center text-xs">
                  {idx + 1}
                </span>
                {memberName}
              </h3>
              <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-50 text-red-600 text-[10px] font-bold uppercase tracking-wider border border-red-100">
                <span className="w-1.5 h-1.5 rounded-full bg-red-500"></span>
                Details Required
              </span>
            </div>

            <div className="p-4 space-y-4">
              {missing.household_member_id && (
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                    Household Member <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={row.household_member_id || ""}
                    onChange={(e) => handleChange(idx, "household_member_id", e.target.value)}
                    className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all"
                  >
                    <option value="">Select member</option>
                    {members.map((m) => {
                      if (ownerMemberIds.has(m.id)) return null;
                      const fn = m.personal_info?.first_name || "";
                      const ln = m.personal_info?.last_name || "";
                      const label = `${fn} ${ln}`.trim() || "Member";
                      return (
                        <option key={m.id} value={m.id}>
                          {label}
                        </option>
                      );
                    })}
                  </select>
                </div>
              )}

              {missing.relationship && (
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                    Relationship to Account Owner <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={row.relationship || ""}
                    onChange={(e) => handleChange(idx, "relationship", e.target.value)}
                    className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all"
                  >
                    <option value="">Select relationship to account owner</option>
                    {RELATIONSHIP_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </div>
        );
      })}

      <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-200">
        <button
          onClick={() => handleSubmit(onSaveAndExit)}
          disabled={saving}
          className="px-6 py-2.5 rounded-lg text-sm font-medium border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 transition-colors disabled:opacity-50"
        >
          Save & Exit
        </button>
        <button
          onClick={() => handleSubmit(onSave)}
          disabled={saving}
          className="px-6 py-2.5 rounded-lg text-sm font-medium bg-emerald-500 text-white hover:bg-emerald-600 transition-colors disabled:opacity-50 flex items-center gap-2"
        >
          {saving && (
            <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
          )}
          {nextLabel || "Save & Next \u2192"}
        </button>
      </div>
    </div>
  );
};

export default TrustedContactsForm;
